import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import Button from "@lhd/components/Button";
import { Form, message, Tooltip } from "antd";
import { useMemo, useCallback } from "react";

const EXCEL_MAX_CELL_LENGTH = 32767;
import { saveAs } from "file-saver";
import { TableData } from "@lhd/types";
import { stringifyCsvMatrix, tableDataToExportMatrix } from "../../untils";

interface IProps {
  tableItem: AppPartType;
  appParts?: AppPartType[];
  tableData?: TableData;
}

const safeFileBase = (item: AppPartType) => {
  const raw = item.field_code || item.label_text || "table_export";
  return (
    String(raw)
      .replace(/[^\w\u3040-\u30ff\u3400-\u9fff-]+/g, "_")
      .slice(0, 80) || "export"
  );
};

const ButtonExport = (props: IProps) => {
  const { tableItem, appParts, tableData } = props;

  const columnParts = useMemo(
    () =>
      [...(tableItem.children || [])].sort(
        (a, b) => a.item_config_data.x - b.item_config_data.x,
      ),
    [tableItem.children],
  );
  const form = Form.useFormInstance();
  const appPartForGetTitle = useMemo(() => {
    return appParts?.find((item) => item.field_code === "規程種別");
  }, [appParts]);

  const getTableTitle = useCallback(() => {
    const raw = form.getFieldValue(appPartForGetTitle?.id || "");
    const title = Array.isArray(raw)
      ? raw.join(",")
      : raw != null
      ? String(raw)
      : "";
    return title || safeFileBase(tableItem);
  }, [form, appPartForGetTitle, tableItem]);

  const exportMatrix = useCallback(() => {
    const tableTitle = getTableTitle();
    return tableDataToExportMatrix(columnParts, tableData, {
      includeHeader: true,
      title: tableTitle || undefined,
    });
  }, [columnParts, tableData]);

  const hasExcelOverflowCell = useMemo(() => {
    if (!columnParts.length) return false;
    const matrix = exportMatrix();
    return matrix.some((row) =>
      row.some(
        (cell) =>
          typeof cell === "string" && cell.length > EXCEL_MAX_CELL_LENGTH,
      ),
    );
  }, [columnParts.length, exportMatrix]);

  const handleExportCsv = useCallback(() => {
    if (!columnParts.length) {
      message.warning("列定義がありません");
      return;
    }
    try {
      const tableTitle = getTableTitle();
      const matrix = exportMatrix();
      const csv = stringifyCsvMatrix(matrix);
      const blob = new Blob(["\uFEFF", csv], {
        type: "text/csv;charset=utf-8;",
      });
      saveAs(blob, `${tableTitle}.csv`);
    } catch (error) {
      console.log("error", error);
      message.error("CSVのエクスポートに失敗しました");
    }
  }, [columnParts.length, exportMatrix, getTableTitle]);

  const handleExportExcel = useCallback(async () => {
    if (!columnParts.length) {
      message.warning("列定義がありません");
      return;
    }
    try {
      const XLSX = await import("xlsx");
      const matrix = exportMatrix();
      const ws = XLSX.utils.aoa_to_sheet(matrix);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buf], { type: "application/octet-stream" }),
        `${getTableTitle()}.xlsx`,
      );
    } catch (error) {
      console.log("error", error);
      message.error("Excelのエクスポートに失敗しました");
    }
  }, [columnParts.length, exportMatrix, getTableTitle]);

  if (!columnParts.length) return null;

  return (
    <>
      <Button label="CSVのエクスポート" height={20} onClick={handleExportCsv} />
      <Tooltip
        title={
          hasExcelOverflowCell ? (
            <div>
              <p>表/図/注記の列に画像が含まれているため、</p>
              <p>Excelへのエクスポートができません。</p>
            </div>
          ) : undefined
        }
      >
        <span>
          <Button
            label="Excelのエクスポート"
            height={20}
            onClick={handleExportExcel}
            disabled={hasExcelOverflowCell}
          />
        </span>
      </Tooltip>
    </>
  );
};

export default ButtonExport;
